import "./css/style.scss";
import { CSSProperties, useEffect, useMemo, useState } from "react";
import {
  CampaignType,
  FilterType,
  ICONS_DEFAULT_VALUES,
  LayoutDevices,
  LayoutDeviceSettings,
  Offer,
  OfferCategory,
  PlacementStage,
  QueryParams,
  QuerySaveEmail,
  SelectedOfferStorage,
  TemplateIcons,
  TemplateOptionSettings,
  groupingDefaultSettings,
} from "../../../types/global.types";
import { getTrackingEvent, postTagEmailOffer } from "../../../services/offers/offers.service";
import TemplateSingle from "../single/TemplateSingle.component";
import LayoutEmail from "../../layout/email";
import PreCountAndSelectAll from "../components/PreCountAndSelectAll/PreCountAndSelectAll";
import TemplateRewardFilters from "../components/Filters/rewards/TemplateRewardFilters";
import TemplateFilters from "../components/Filters/desktop/TemplateFilters";
import SwiperComponent from "./swiper/Swiper.component";
import TemplateIcon from "../components/Icons/info.component";
import FeaturedOfferSmall from "../components/FeaturedSlots/FeaturedOfferSmall/FeaturedOfferSmall";
import { MultipleFeaturedWrapperDesktop } from "../components/FeaturedSlots/MultipleFeaturedWrapperDesktop/MultipleFeaturedWrapperDesktop";
import FeaturedOfferLarge from "../components/FeaturedSlots/FeaturedOfferLarge/FeaturedOfferLarge";
import MultipleFeaturedWrapperMobile from "../components/FeaturedSlots/MultipleFeaturedWrapperMobile/MultipleFeaturedWrapperMobile";
import FooterText from "../components/FooterText/FooterText";
import RedirectionButton from "../components/Redirection/redirectionButton.component";
import EmailAutomaticTermsBanner from "../components/EmailAutomaticTermsBanner/EmailAutomaticTermsBanner.component";
import { getHeaderEmailAutomaticTermsText } from "../utils/emailAutomaticTerms.utils";
import { EMAIL_REGEX } from "../../../utility/regexp";
import { getStorageItem, updateStorageItem } from "../../../utility/storage";
import { TEMPLATE_CATEGORY } from "../../../utility/template";

const getVisibleOffers = (offers: Offer[]) =>
  offers.filter((o) => !o.tagBlocked?.isBlocked);

interface Props {
  params: QueryParams;
  layoutSettings: LayoutDeviceSettings;
  campaignId: string;
  placementId: string;
  placementName: string;
  campaignRes: CampaignType;
  selectedOffers: string[];
  singleOffer: Offer | undefined;
  isEmail: boolean;
  isSave: boolean | undefined;
  isShare: boolean | undefined;
  email: string | undefined;
  slot: number;
  sessionId: string;
  isSeen: boolean;
  setSingle: React.Dispatch<React.SetStateAction<Offer | undefined>>;
  setIsEmail: React.Dispatch<React.SetStateAction<boolean>>;
  layoutId: string;
  device: LayoutDevices;
}

const PreviewCategory = ({
  params,
  layoutSettings,
  isSave,
  isShare,
  campaignRes,
  campaignId,
  placementId,
  placementName,
  selectedOffers,
  singleOffer,
  setSingle,
  setIsEmail,
  isEmail,
  email,
  slot,
  isSeen,
  sessionId,
  layoutId,
  device,
}: Props) => {
  const settingsTemplate = layoutSettings?.template;
  const settings: TemplateOptionSettings | undefined = layoutSettings.settings;
  const [displayOnly] = useState<boolean>(
    campaignRes?.placementSettings[0]?.displayOnly
  );
  const [campaign, setCampaign] = useState<CampaignType | undefined>(
    campaignRes
  );
  const [preEmail, setPreEmail] = useState<string | undefined>(email);
  const [isSingleSave, setSingleSave] = useState<boolean>(false);
  const [hasOpen, setHasOpen] = useState<boolean>(false);
  const [offerFilteredList, setOfferFilteredList] = useState<Offer[]>(
    !displayOnly ? getVisibleOffers(campaignRes.offers) : campaignRes.offers
  );
  const [autoSendingOfferId, setAutoSendingOfferId] = useState<string>("");
  const featuredOffers = useMemo(() => {
    if (!settings?.mainLayout.showFeatured) return [];
    return offerFilteredList
      .filter((o) => o.slot <= -10)
      .sort((a, b) => b.slot - a.slot);
  }, [offerFilteredList, settings?.mainLayout.showFeatured]);

  const gridOffers = useMemo(() => {
    return settings?.mainLayout.showFeatured
      ? offerFilteredList.filter((o) => o.slot > -10)
      : offerFilteredList;
  }, [offerFilteredList, settings?.mainLayout.showFeatured]);

  const groupedSections = useMemo(() => {
    const grouping = {
      ...groupingDefaultSettings,
      ...settings?.mainLayout?.grouping,
    };

    const sections: { key: string; title: string; offers: Offer[] }[] = [];
    const usedOfferIds = new Set<string>();

    if (grouping.trending) {
      const trendingOffers = gridOffers
        .slice(0, grouping.trendingCount);

      trendingOffers.forEach((o) => usedOfferIds.add(o.id));

      if (trendingOffers.length > 0) {
        sections.push({
          key: "trending",
          title: grouping.trendingText,
          offers: trendingOffers,
        });
      }
    }

    const categoryMap = new Map<string, Offer[]>();
    const uncategorized: Offer[] = [];

    gridOffers.forEach((offer) => {
      if (usedOfferIds.has(offer.id)) return;

      if (!offer.categories?.length) {
        uncategorized.push(offer);
        usedOfferIds.add(offer.id);
        return;
      }

      const primaryCategory = [...offer.categories]
        .sort((a: OfferCategory, b: OfferCategory) =>
          a.name.localeCompare(b.name)
        )[0].name;

      if (!categoryMap.has(primaryCategory)) {
        categoryMap.set(primaryCategory, []);
      }
      categoryMap.get(primaryCategory)!.push(offer);
      usedOfferIds.add(offer.id);
    });

    Array.from(categoryMap.keys())
      .sort((a, b) => a.localeCompare(b))
      .forEach((name) => {
        sections.push({
          key: `cat-${name}`,
          title: name,
          offers: categoryMap.get(name)!,
        });
      });

    if (uncategorized.length > 0) {
      sections.push({
        key: "uncategorized",
        title: "Other",
        offers: uncategorized,
      });
    }

    return sections;
  }, [gridOffers, settings?.mainLayout?.grouping]);

  const shouldLimitFilterPopup = useMemo(() => {
    const visibleContentRows =
      groupedSections.length + (featuredOffers.length > 0 ? 1 : 0);

    return (
      visibleContentRows === 1 &&
      groupedSections.some((section) => section.offers.length > 0)
    );
  }, [groupedSections, featuredOffers.length]);

  const updateCampaignBySingle = (o: Offer) => {
    if (campaign) {
      const newOffers: Offer[] = campaign.offers.map((offer: Offer) => {
        if (offer.id === o.id) {
          return o;
        }
        return offer;
      });

      setCampaign(
        (prevState) => prevState && { ...prevState, offers: newOffers }
      );
      setOfferFilteredList(newOffers);
    }
  };

  const updateFilteredOffers = (newOffers: Offer[], currentOffers: Offer[]) => {
    const currentOfferIds: string[] = currentOffers.map((i) => i.id);
    setOfferFilteredList(
      newOffers.filter((f) => currentOfferIds.includes(f.id))
    );
  };

  useEffect(() => {
    window.addEventListener("message", function (event) {
      if (Object.keys(event.data).includes("single")) {
        updateCampaignBySingle(event.data.single);
      }
    });

    return () => {
      window.removeEventListener("message", () => { });
    };
    // eslint-disable-next-line
  }, [campaign]);

  const resolvedEmailForAutoSend = useMemo(() => {
    if (preEmail?.trim()) {
      return preEmail.trim();
    }
    if (email?.trim()) {
      return email.trim();
    }

    const cookieOptions = campaign?.cookieOptions || {
      allowCookies: false,
      allowSessionCookies: false,
      allowPersistentCookies: false,
      expiryDays: "1000",
    };
    return getStorageItem("brswEmail", cookieOptions)?.trim() || "";
  }, [campaign?.cookieOptions, email, preEmail]);

  const canAutoSendEmailFromLabel =
    campaign?.campaignGeneralOptions?.manualEmailEntry === false &&
    Boolean(resolvedEmailForAutoSend) &&
    EMAIL_REGEX.test(resolvedEmailForAutoSend);

  const canAutoSendEmailFromMainCTA =
    campaign?.campaignGeneralOptions?.autoSendEmail === true &&
    campaign?.campaignGeneralOptions?.manualEmailEntry === false &&
    Boolean(resolvedEmailForAutoSend) &&
    EMAIL_REGEX.test(resolvedEmailForAutoSend);

  const handleAutoSendFromOfferLabel = async (offer: Offer, slot: number) => {
    if (
      offer.offerSettings?.bundleOptions?.isBundled ||
      !resolvedEmailForAutoSend.trim()
    ) {
      return;
    }

    setAutoSendingOfferId(offer.id);
    const emailParams: QuerySaveEmail = {
      offerid: offer.id,
      email: resolvedEmailForAutoSend,
      offerLink: offer.trackingUrl,
      companyId: campaign?.company_Id || "",
      placementId,
      campaignId,
      templateId: settingsTemplate.identifier,
      slot,
      orderId: offer.orderId,
      pltype: displayOnly ? PlacementStage.pre : PlacementStage.post,
      layoutId,
    };

    try {
      const response = await postTagEmailOffer(emailParams);
      getTrackingEvent({
        event: "onsendemailclick",
        retailerId: campaign?.company_Id,
        campaignId,
        offerId: offer.id,
        placementId,
        placementName,
        tid: settingsTemplate.identifier,
        slot,
        pltype: displayOnly ? PlacementStage.pre : PlacementStage.post,
        pltid: layoutId,
      });

      if (response?.data?.status) {
        updateStorageItem(
          "brswEmail",
          resolvedEmailForAutoSend,
          campaign?.cookieOptions
        );
        setPreEmail(resolvedEmailForAutoSend);
      }
    } finally {
      setAutoSendingOfferId("");
    }
  };

  const handleCloseSingle = () => {
    setSingle(undefined);
    setSingleSave(false);
  };

  const checkSelectedOffers = (
    selectedOfferIds: string[] = [],
    _campaign: CampaignType
  ) => {
    updateFilteredOffers(_campaign.offers.map((offer: Offer) => {
      return { ...offer, isAdded: selectedOfferIds.includes(offer.id) };
    }), offerFilteredList);
  };

  const openSingle = (
    offer: Offer,
    isSave: boolean,
    isAutoEmail: boolean,
    slot: number,
    isShare?: boolean
  ) => {
    if (isAutoEmail) {
      const params = { offer, isSave, isAuto: true, slot };
      window.top?.postMessage(
        { event: "emailPopupEmbedded", load: params },
        "*"
      );
    } else {
      window.parent.postMessage(
        {
          event: "openSingleEmbedded",
          load: { offer, isSave, slot, isShare: isShare || false },
        },
        "*"
      );
    }
  };

  const handleAdd = (offer: Offer, value: boolean, slot: number) => {
    setSelectedOfferStorage({
      id: offer.id,
      link: offer.trackingUrl,
      active: value,
      layoutId: layoutId || "",
    });
    if (campaign) {
      const newOffers: Offer[] = campaign.offers.map((_offer: Offer) => {
        if (_offer.id === offer.id) {
          _offer.isAdded = value;
        }
        return _offer;
      });
      setCampaign(
        (prevState) => prevState && { ...prevState, offers: newOffers }
      );
      updateFilteredOffers(newOffers, offerFilteredList);
    } else {
      const _single = { ...singleOffer, isAdded: value };
      setSingle((prevState) => prevState && { ...prevState, isAdded: value });
      window.top?.postMessage(
        { event: "singleChange", offer: _single, slot },
        "*"
      );
    }

    value &&
      campaign &&
      getTrackingEvent({
        event: "onselectoffer",
        offerId: offer.id,
        retailerId: campaign.company_Id,
        campaignId: campaign.id,
        placementId,
        placementName,
        tid: settingsTemplate.identifier,
        slot,
        pltype: displayOnly ? PlacementStage.pre : PlacementStage.post,
        pltid: layoutId,
      });
  };

  const handleSelectAll = () => {
    if (!campaign) {
      return;
    }

    const selectedLength = offerFilteredList.filter(
      (o) => selectedOffers.includes(o.id) && !o.tagBlocked?.isBlocked
    ).length;
    const offerLength = offerFilteredList.filter(
      (o) => !o.tagBlocked?.isBlocked
    ).length;
    const shouldRemove = selectedLength === offerLength;

    // Batch update all offers at once instead of individual handleAdd calls
    const offersToUpdate = offerFilteredList.filter(
      (offer) =>
        !offer.tagBlocked?.isBlocked &&
        (shouldRemove ? offer.isAdded : !offer.isAdded)
    );

    if (offersToUpdate.length === 0) {
      return;
    }

    // Update campaign state with all changes at once
    const newOffers: Offer[] = campaign.offers.map((offer: Offer) => {
      const shouldUpdate = offersToUpdate.some((o) => o.id === offer.id);
      if (shouldUpdate) {
        return { ...offer, isAdded: !shouldRemove };
      }
      return offer;
    });

    setCampaign(
      (prevState) => prevState && { ...prevState, offers: newOffers }
    );
    updateFilteredOffers(newOffers, offerFilteredList);

    // Batch send storage updates for all offers using setSelectedOffersStorage
    const storageUpdates: SelectedOfferStorage[] = offersToUpdate.map(
      (offer) => ({
        id: offer.id,
        link: offer.trackingUrl,
        active: !shouldRemove,
        layoutId: layoutId || "",
      })
    );

    // Send all storage updates at once using the new batch method
    setSelectedOffersStorage(storageUpdates);

    // Batch tracking events for selected offers (only when adding, not removing)
    if (!shouldRemove && campaign) {
      offersToUpdate.forEach((offer) => {
        getTrackingEvent({
          event: "onselectoffer",
          offerId: offer.id,
          retailerId: campaign.company_Id,
          campaignId: campaign.id,
          placementId,
          placementName,
          tid: settingsTemplate.identifier,
          slot: offer.slot,
          pltype: displayOnly ? PlacementStage.pre : PlacementStage.post,
          pltid: layoutId,
        });
      });
    }
  };

  const setSelectedOfferStorage = (storage: SelectedOfferStorage) => {
    window.top?.postMessage({ event: "setSelectedOffer", load: storage }, "*");
  };

  const setSelectedOffersStorage = (storage: SelectedOfferStorage[]) => {
    window.top?.postMessage({ event: "setSelectedOffers", load: storage }, "*");
  };

  useEffect(() => {
    if (campaignRes) {
      setCampaign(campaignRes);
      if (campaignRes && !singleOffer) {
        !hasOpen &&
          getTrackingEvent({
            event: "ontemplateopen",
            retailerId: campaignRes.company_Id,
            campaignId: campaignRes.id,
            placementId,
            placementName,
            tid: settingsTemplate.identifier,
            pltype: displayOnly ? PlacementStage.pre : PlacementStage.post,
            pltid: layoutId,
          });
      }
      setHasOpen(true);
    }
    // eslint-disable-next-line
  }, [campaignRes]);

  useEffect(() => {
    checkSelectedOffers(selectedOffers, campaignRes);
    // eslint-disable-next-line
  }, [selectedOffers, campaignRes]);

  useEffect(() => {
    document.body.classList.add("no-scroll");
  }, []);

  const renderFeaturedOffers = () => {
    if (
      campaign &&
      settings &&
      featuredOffers.length > 0 &&
      settings?.mainLayout.showFeatured
    ) {
      if (device === LayoutDevices.mobile) {
        return (
          <MultipleFeaturedWrapperMobile
            featuredOffers={featuredOffers}
            settings={settings}
            settingsTemplate={settingsTemplate}
            displayOnly={displayOnly}
            campaignId={campaign.id}
            placementId={placementId}
            placementName={placementName}
            preEmail={preEmail || email}
            setPreEmail={setPreEmail}
            campaign={campaign}
            openSingle={openSingle}
            onAdd={handleAdd}
            layoutId={layoutId}
            position={settings?.mainLayout?.featurePosition || "top"}
            canAutoSendEmailFromLabel={canAutoSendEmailFromLabel}
            canAutoSendEmailFromMainCTA={canAutoSendEmailFromMainCTA}
            onAutoSendEmailFromLabel={handleAutoSendFromOfferLabel}
            autoSendingOfferId={autoSendingOfferId}
          />
        );
      } else {
        if (featuredOffers.length > 1) {
          return (
            <MultipleFeaturedWrapperDesktop
              featuredOffers={featuredOffers}
              settings={settings}
              settingsTemplate={settingsTemplate}
              displayOnly={displayOnly}
              campaignId={campaign.id}
              placementId={placementId}
              placementName={placementName}
              preEmail={preEmail || email}
              setPreEmail={setPreEmail}
              campaign={campaign}
              openSingle={openSingle}
              onAdd={handleAdd}
              layoutId={layoutId}
              canAutoSendEmailFromLabel={canAutoSendEmailFromLabel}
              canAutoSendEmailFromMainCTA={canAutoSendEmailFromMainCTA}
              onAutoSendEmailFromLabel={handleAutoSendFromOfferLabel}
              autoSendingOfferId={autoSendingOfferId}
            />
          );
        } else {
          return featuredOffers.map((featuredOffer) => (
            <div className="super-desktop-feature" key={featuredOffer.id}>
              {!settings?.mainLayout?.featuredSize ||
                settings?.mainLayout?.featuredSize === "large" ? (
                <FeaturedOfferLarge
                  slot={featuredOffer.slot}
                  settingsTemplate={settingsTemplate}
                  mainSettings={settings?.mainLayout}
                  campaignId={campaign.id}
                  placementId={placementId}
                  placementName={placementName}
                  offer={featuredOffer}
                  offerLength={campaign.offers.length}
                  settings={settings?.dealLayout}
                  defaultOpen={false}
                  sessionId={sessionId}
                  openSingle={openSingle}
                  onAdd={handleAdd}
                  setPreEmail={setPreEmail}
                  preEmail={preEmail || email}
                  displayOnly={displayOnly}
                  campaign={campaign}
                  isAdded={featuredOffer.isAdded || false}
                  retailerId={campaign.company_Id}
                  hasVoucher={
                    (featuredOffer.voucher &&
                      !!featuredOffer.voucher.voucherType) ||
                    false
                  }
                  otherSettings={settings.otherSettings}
                  layoutId={layoutId}
                  layoutOptions={settings.layoutOptions}
                  canAutoSendEmailFromLabel={canAutoSendEmailFromLabel}
                  canAutoSendEmailFromMainCTA={canAutoSendEmailFromMainCTA}
                  onAutoSendEmailFromLabel={handleAutoSendFromOfferLabel}
                  autoSendingOfferId={autoSendingOfferId}
                />
              ) : (
                <FeaturedOfferSmall
                  sessionId={sessionId}
                  slot={featuredOffer.slot}
                  settingsTemplate={settingsTemplate}
                  mainSettings={settings?.mainLayout}
                  campaignId={campaign.id}
                  placementId={placementId}
                  placementName={placementName}
                  offer={featuredOffer}
                  offerLength={campaign.offers.length}
                  settings={settings?.dealLayout}
                  defaultOpen={false}
                  openSingle={openSingle}
                  onAdd={handleAdd}
                  setPreEmail={setPreEmail}
                  preEmail={preEmail || email}
                  displayOnly={displayOnly}
                  campaign={campaign}
                  isAdded={featuredOffer.isAdded || false}
                  retailerId={campaign.company_Id}
                  hasVoucher={
                    (featuredOffer.voucher &&
                      !!featuredOffer.voucher.voucherType) ||
                    false
                  }
                  otherSettings={settings.otherSettings}
                  layoutId={layoutId}
                  layoutOptions={settings.layoutOptions}
                  canAutoSendEmailFromLabel={canAutoSendEmailFromLabel}
                  canAutoSendEmailFromMainCTA={canAutoSendEmailFromMainCTA}
                  onAutoSendEmailFromLabel={handleAutoSendFromOfferLabel}
                  autoSendingOfferId={autoSendingOfferId}
                />
              )}
            </div>
          ));
        }
      }
    }
  };

  const svgAnimationKey = [
    settings?.mainLayout?.iconAnimation,
    settings?.mainLayout?.iconAnimationType,
    settings?.mainLayout?.iconAnimationRepeat,
  ].join("-");

  const svgAnimationStyle = settings?.mainLayout?.iconAnimation
    ? {
      animation: `${settings?.mainLayout?.iconAnimationType} 1s linear`,
      animationIterationCount: settings?.mainLayout?.iconAnimationRepeat,
      display: "inline-flex",
    }
    : {};

  const resolvedEmailForBanner =
    preEmail || email || params?.contactDetails?.[0]?.email;
  const headerEmailAutomaticTermsText = getHeaderEmailAutomaticTermsText(
    settings?.mainLayout,
    campaign?.campaignGeneralOptions,
    settingsTemplate.identifier
  );

  return (
    <>
      {singleOffer && (
        <>
          {isEmail ? (
            <LayoutEmail
              settings={layoutSettings.popup}
              templateSettings={layoutSettings.settings}
              emailPopupOffer={singleOffer}
              isEmbedded={true}
              setIsEmail={setIsEmail}
              setSingle={setSingle}
              prePopulateEmail={preEmail}
              campaignObj={campaignRes}
              campaignId={campaignRes.id}
              placementId={placementId}
              placementName={placementName}
              slot={slot}
              layoutId={layoutId}
            ></LayoutEmail>
          ) : (
            <>
              {campaign?.id && settings && (
                <TemplateSingle
                  slot={slot}
                  settings={settings}
                  isInline={true}
                  offer={singleOffer}
                  isSave={isSave || isSingleSave}
                  isShare={isShare || false}
                  placementId={placementId}
                  placementName={placementName}
                  settingsTemplate={settingsTemplate}
                  campaignObj={campaign}
                  displayOnly={displayOnly}
                  campaignId={campaign?.id}
                  companyId={campaign.company_Id}
                  updateCampaign={handleAdd}
                  setPreEmail={setPreEmail}
                  preEmail={preEmail || email}
                  closeSignle={handleCloseSingle}
                  layoutId={layoutId}
                />
              )}
            </>
          )}
        </>
      )}
      <div className="template-category">
        <div
          className={`inline-wrapper ${device} ${settings?.mainLayout?.align} ${singleOffer || isEmail ? "hidden" : "displayed"
            }`}
          aria-label="Special offers and promotions"
          style={{
            width: settings?.embeddedElement?.fullWidth
              ? "100%"
              : settings?.embeddedElement?.width,
            padding: `0 ${settings?.embeddedElement?.margin || "0"}${settings?.embeddedElement?.marginType || "%"
              }`,
          }}
        >
          <div
            className={`inline-popup`}
            style={{
              padding: `${typeof settings?.mainLayout.paddingTopBottom !== "undefined"
                ? settings?.mainLayout.paddingTopBottom
                : 20
                }px ${typeof settings?.mainLayout.paddingLeftRight !== "undefined"
                  ? settings?.mainLayout.paddingLeftRight
                  : 35
                }px`,
              backgroundColor: settings?.mainLayout.backgroundColor,
              fontFamily: settings?.mainLayout.fontFamily,
              color: settings?.mainLayout.textColor,
              height:
                settings?.mainLayout.offersNumber === "auto" || TEMPLATE_CATEGORY.includes(settingsTemplate.identifier || "")
                  ? `auto`
                  : `${settings?.embeddedElement?.height}px`,
            }}
          >
            <div
              style={{
                maxWidth:
                  settings?.mainLayout.offersNumber === "auto"
                    ? `${settings?.embeddedElement?.maxWidth || 100}${settings?.embeddedElement?.maxWidthType || "%"
                    }`
                    : "100%",
                margin: "auto",
              }}
            >
              {settings?.mainLayout.headerHTML && (
                <div
                  className="inline-header-html"
                  dangerouslySetInnerHTML={{
                    __html: settings?.mainLayout.headerHTML,
                  }}
                ></div>
              )}
              <div className={`inline-title`}>
                {settings?.mainLayout?.headerIcon === "gift" && (
                  <>
                    <div
                      className="inline-title-icon"
                      key={svgAnimationKey}
                      style={svgAnimationStyle}
                    >
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M17.2 10V19H2.8V10M10 19V5.5M10 5.5H5.95C5.35326 5.5 4.78097 5.26295 4.35901 4.84099C3.93705 4.41903 3.7 3.84674 3.7 3.25C3.7 2.65326 3.93705 2.08097 4.35901 1.65901C4.78097 1.23705 5.35326 1 5.95 1C9.1 1 10 5.5 10 5.5ZM10 5.5H14.05C14.6467 5.5 15.219 5.26295 15.641 4.84099C16.0629 4.41903 16.3 3.84674 16.3 3.25C16.3 2.65326 16.0629 2.08097 15.641 1.65901C15.219 1.23705 14.6467 1 14.05 1C10.9 1 10 5.5 10 5.5ZM1 5.5H19V10H1V5.5Z"
                          stroke={settings?.mainLayout.headerTextColor}
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                  </>
                )}

                {settings?.mainLayout?.headerIcon === "lock" && (
                  <>
                    <div
                      className="inline-title-icon"
                      key={svgAnimationKey}
                      style={svgAnimationStyle}
                    >
                      {displayOnly ? (
                        <svg
                          width="42"
                          height="47"
                          viewBox="0 0 42 47"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M9.88889 21.25V12.25C9.88889 9.26631 11.0595 6.40483 13.1433 4.29505C15.227 2.18526 18.0532 1 21 1C23.9469 1 26.773 2.18526 28.8567 4.29505C30.9405 6.40483 32.1111 9.26631 32.1111 12.25V21.25M5.44444 21.25H36.5556C39.0102 21.25 41 23.2647 41 25.75V41.5C41 43.9853 39.0102 46 36.5556 46H5.44444C2.98985 46 1 43.9853 1 41.5V25.75C1 23.2647 2.98985 21.25 5.44444 21.25Z"
                            stroke={settings?.mainLayout.headerTextColor}
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      ) : (
                        <svg
                          width="42"
                          height="47"
                          viewBox="0 0 42 47"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M9.88889 21.2554V12.258C9.88613 9.46896 10.9073 6.77834 12.7542 4.7085C14.601 2.63865 17.1419 1.33726 19.8834 1.05697C22.6248 0.776667 25.3714 1.53746 27.5899 3.19164C29.8084 4.84583 31.3406 7.2754 31.8889 10.0087M5.44444 21.2572H36.5556C39.0102 21.2572 41 23.2714 41 25.7559V41.5013C41 43.9859 39.0102 46 36.5556 46H5.44444C2.98985 46 1 43.9859 1 41.5013V25.7559C1 23.2714 2.98985 21.2572 5.44444 21.2572Z"
                            stroke={settings?.mainLayout.headerTextColor}
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </div>
                  </>
                )}

                {settings?.mainLayout?.headerIcon === "custom" &&
                  settings?.mainLayout?.headerCustomIcon && (
                    <div
                      className="inline-title-icon"
                      key={svgAnimationKey}
                      style={svgAnimationStyle}
                      dangerouslySetInnerHTML={{
                        __html: settings.mainLayout.headerCustomIcon,
                      }}
                    />
                  )}

                <span
                  className={`inline-title-label`}
                  style={{
                    fontSize: settings?.mainLayout.headerFontSize + "px",
                    fontWeight: settings?.mainLayout.headerFontWeight,
                    color: settings?.mainLayout.headerTextColor,
                  }}
                  tabIndex={0}
                  role="contentinfo"
                  aria-label={settings?.mainLayout.headerText || ""}
                >
                  {settings?.mainLayout.headerText || ""}
                </span>
              </div>

              <div
                className={`inline-description`}
                style={{
                  fontSize: settings?.mainLayout.subHeaderFontSize + "px",
                  fontWeight: settings?.mainLayout.subHeaderFontWeight,
                  color: settings?.mainLayout.subHeaderTextColor,
                }}
                tabIndex={0}
                role="contentinfo"
                aria-label={settings?.mainLayout.subHeaderText || ""}
              >
                {settings?.mainLayout.subHeaderText || ""}
              </div>

              <EmailAutomaticTermsBanner
                campaignGeneralOptions={campaign?.campaignGeneralOptions}
                cookieOptions={campaign?.cookieOptions}
                text={headerEmailAutomaticTermsText}
                email={resolvedEmailForBanner}
                icon={settings?.dealLayout?.iconInfo?.value}
                onEmailChange={setPreEmail}
                mobile={device === LayoutDevices.mobile}
              />

              {settings?.filters?.filters && campaign && (
                <>
                  {(settings.filters.filterType ?? FilterType.popup) ===
                    FilterType.popup && (
                      <TemplateRewardFilters
                        campaign={campaign}
                        settings={settings}
                        setFilteredList={setOfferFilteredList}
                        limitPopupHeight={shouldLimitFilterPopup}
                      />
                    )}

                  {settings.filters.filterType === FilterType.row && (
                    <TemplateFilters
                      campaign={campaign}
                      settings={settings}
                      setFilteredList={setOfferFilteredList}
                    />
                  )}
                </>
              )}

              {displayOnly &&
                campaign?.campaignGeneralOptions?.showAddToOrder && (
                  <PreCountAndSelectAll
                    offerLength={
                      offerFilteredList.filter((o) => !o.tagBlocked?.isBlocked)
                        .length
                    }
                    countText={settings?.otherSettings?.counterText}
                    selectText={settings?.otherSettings?.selectedText}
                    showCheckboxSelect={
                      settings?.otherSettings?.showCheckboxSelect || false
                    }
                    showSelectAll={
                      settings?.otherSettings?.showSelectAll || false
                    }
                    showCount={settings?.otherSettings?.showCounter || false}
                    noOfSelected={
                      offerFilteredList.filter(
                        (o) =>
                          selectedOffers.includes(o.id) &&
                          !o.tagBlocked?.isBlocked
                      ).length
                    }
                    handleSelectAll={handleSelectAll}
                    highlightColor={settings?.mainLayout?.highlightColor}
                    borderColor={settings?.dealLayout?.borderColor}
                  />
                )}

              <div id="inline-grid">
                {renderFeaturedOffers()}
                <div className="inline-grid">
                  {campaign && settings && groupedSections.length > 0 && (
                    <div className="category-groups">
                      {groupedSections.map((section) => (
                        <div
                          key={section.key}
                          className={`category-swiper swiper-column-${settings?.mainLayout.offersNumber || 3
                            }`}
                          style={
                            {
                              "--swiper-pagination-color":
                                settings?.mainLayout.textColor,
                              "--swiper-pagination-bullet-inactive-color": `${settings?.mainLayout.textColor}e0`,
                            } as CSSProperties
                          }
                        >
                          <div className="category-swiper-title">
                            <span>{section.title}</span>
                            <span className="category-swiper-title-count">
                              {section.offers.length}{" "}
                              {section.offers.length === 1 ? "offer" : "offers"}
                            </span>
                          </div>
                          <SwiperComponent
                            sessionId={sessionId}
                            offers={section.offers}
                            settings={settings}
                            displayOnly={displayOnly}
                            campaign={campaign}
                            settingsTemplate={settingsTemplate}
                            campaignId={campaign.id}
                            placementId={placementId}
                            placementName={placementName}
                            openSingle={openSingle}
                            onAdd={handleAdd}
                            setPreEmail={setPreEmail}
                            preEmail={preEmail || email}
                            isSeen={isSeen}
                            layoutId={layoutId}
                            canAutoSendEmailFromLabel={canAutoSendEmailFromLabel}
                            canAutoSendEmailFromMainCTA={canAutoSendEmailFromMainCTA}
                            onAutoSendEmailFromLabel={handleAutoSendFromOfferLabel}
                            autoSendingOfferId={autoSendingOfferId}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <RedirectionButton
                  settings={settings}
                  displayOnly={displayOnly}
                />

                <div className="inline-footer">
                  {displayOnly ? (
                    <>
                      {campaign?.campaignGeneralOptions
                        .emailCaptureOnOffers && (
                          <>
                            <div role="img" aria-label="Information">
                              {!settings?.dealLayout?.icons ||
                                settings?.dealLayout?.icons ===
                                TemplateIcons.default ? (
                                <TemplateIcon
                                  icon={
                                    settings?.dealLayout.iconInfo?.value ||
                                    ICONS_DEFAULT_VALUES.iconInfo
                                  }
                                  color={
                                    settings?.mainLayout.textColor || "#000000"
                                  }
                                />
                              ) : (
                                <svg
                                  width="24"
                                  height="24"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M12 0C5.37 0 0 5.37 0 12C0 18.63 5.37 24 12 24C18.63 24 24 18.63 24 12C24 5.37 18.63 0 12 0ZM13.5 18.5C13.5 19.33 12.83 20 12 20C11.17 20 10.5 19.33 10.5 18.5V10.5C10.5 9.67 11.17 9 12 9C12.83 9 13.5 9.67 13.5 10.5V18.5ZM12 7C11.17 7 10.5 6.33 10.5 5.5C10.5 4.67 11.17 4 12 4C12.83 4 13.5 4.67 13.5 5.5C13.5 6.33 12.83 7 12 7Z"
                                    fill={settings?.mainLayout.textColor}
                                  />
                                </svg>
                              )}
                            </div>
                            <FooterText
                              displayOnly={displayOnly}
                              footerText={settings?.mainLayout.footerText}
                              hasTooltip={false}
                            />
                          </>
                        )}
                    </>
                  ) : (
                    <>
                      <div role="img" aria-label="Information">
                        {!settings?.dealLayout?.icons ||
                          settings?.dealLayout?.icons ===
                          TemplateIcons.default ? (
                          <TemplateIcon
                            icon={
                              settings?.dealLayout.iconEmail?.value ||
                              ICONS_DEFAULT_VALUES.iconEmail
                            }
                            color={settings?.mainLayout.textColor || "#000000"}
                          />
                        ) : (
                          <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M12 0C5.37 0 0 5.37 0 12C0 18.63 5.37 24 12 24C18.63 24 24 18.63 24 12C24 5.37 18.63 0 12 0ZM13.5 18.5C13.5 19.33 12.83 20 12 20C11.17 20 10.5 19.33 10.5 18.5V10.5C10.5 9.67 11.17 9 12 9C12.83 9 13.5 9.67 13.5 10.5V18.5ZM12 7C11.17 7 10.5 6.33 10.5 5.5C10.5 4.67 11.17 4 12 4C12.83 4 13.5 4.67 13.5 5.5C13.5 6.33 12.83 7 12 7Z"
                              fill={settings?.mainLayout.textColor}
                            />
                          </svg>
                        )}
                      </div>
                      <FooterText
                        displayOnly={displayOnly}
                        footerText={settings?.mainLayout.footerText}
                        hasTooltip={false}
                      />
                    </>
                  )}
                </div>
                {settings?.mainLayout.footerHTML && (
                  <div
                    className="inline-footer-html"
                    dangerouslySetInnerHTML={{
                      __html: settings?.mainLayout.footerHTML,
                    }}
                  ></div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PreviewCategory;
